import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isValidPackagingQuantity } from '../utils/packagingQuantity';

const OPTIONS = Array.from({ length: 100 }, (_, index) => index + 1);

export default function PackagingQuantityCombobox({
  value,
  onChange,
  max,
  label,
  compact = false,
}: {
  value: string;
  onChange: (value: string) => void;
  max: number;
  label: string;
  compact?: boolean;
}) {
  const id = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const [position, setPosition] = useState({ left: 0, top: 0, width: 0, maxHeight: 240 });
  const valid = isValidPackagingQuantity(value, max);
  const listId = `${id}-list`;
  const errorId = `${id}-error`;

  const openList = () => {
    setActive(isValidPackagingQuantity(value, 100) ? Number(value) : 1);
    setOpen(true);
  };
  const select = (quantity: number) => {
    onChange(String(quantity));
    setOpen(false);
    inputRef.current?.focus();
  };

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop ?? 0;
      const viewportBottom = viewportTop + (viewport?.height ?? window.innerHeight);
      const below = viewportBottom - rect.bottom - 8;
      const above = rect.top - viewportTop - 8;
      const upwards = below < 240 && above > below;
      const height = Math.max(0, Math.min(240, upwards ? above : below));
      setPosition({
        left: rect.left,
        top: upwards ? rect.top - height - 4 : rect.bottom + 4,
        width: rect.width,
        maxHeight: height,
      });
    };
    const onScroll = (event: Event) => {
      if (event.target instanceof Node && listRef.current?.contains(event.target)) return;
      updatePosition();
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', onScroll, true);
    window.visualViewport?.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('scroll', updatePosition);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', onScroll, true);
      window.visualViewport?.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('scroll', updatePosition);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || active === null) return;
    const option = document.getElementById(`${id}-option-${active}`);
    const list = listRef.current;
    if (!option || !list) return;
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
    }
  }, [open, active, id, position.maxHeight]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node
        && !containerRef.current?.contains(event.target)
        && !listRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div ref={containerRef} className="min-w-0 flex-1" dir="rtl">
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          role="combobox"
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={open && active !== null ? `${id}-option-${active}` : undefined}
          aria-invalid={!valid}
          aria-describedby={!valid ? errorId : undefined}
          value={value}
          onChange={event => {
            onChange(event.target.value);
            setActive(null);
          }}
          onClick={() => { if (!open) openList(); }}
          onBlur={event => {
            if (!containerRef.current?.contains(event.relatedTarget)
              && !listRef.current?.contains(event.relatedTarget)) setOpen(false);
          }}
          onKeyDown={event => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              if (!open) openList();
              else setActive(current => Math.min(100, Math.max(1, (current ?? (event.key === 'ArrowDown' ? 0 : 101)) + (event.key === 'ArrowDown' ? 1 : -1))));
            } else if (event.key === 'Enter' && open) {
              event.preventDefault();
              if (active !== null) select(active);
              else setOpen(false);
            } else if (event.key === 'Escape' && open) {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
            } else if (event.key === 'Tab') {
              setOpen(false);
            }
          }}
          className={`w-full min-w-0 border-2 rounded-xl bg-white pe-3 ps-11 text-center font-bold outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 ${compact ? 'py-1 text-lg' : 'py-2 text-xl'} ${valid ? 'border-gray-300' : 'border-red-500'}`}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label="פתח רשימת כמויות"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          onMouseDown={event => event.preventDefault()}
          onClick={() => {
            inputRef.current?.focus();
            if (open) setOpen(false);
            else openList();
          }}
          className="absolute inset-y-0 start-0 w-11 rounded-s-xl text-purple-700 hover:bg-purple-50 touch-manipulation"
        >
          <span aria-hidden="true">▾</span>
        </button>
      </div>
      {!valid && <p id={errorId} role="alert" className="mt-1 text-sm font-bold text-red-700">יש להזין מספר שלם בין 1 ל-{max.toLocaleString('he-IL')}.</p>}
      {open && createPortal(
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={label}
          dir="rtl"
          style={position}
          className="fixed z-[100] overflow-y-auto overscroll-contain rounded-xl border-2 border-purple-300 bg-white shadow-xl"
          onMouseDown={event => event.preventDefault()}
          onClick={event => event.stopPropagation()}
        >
          {OPTIONS.map(quantity => (
            <div
              key={quantity}
              id={`${id}-option-${quantity}`}
              role="option"
              aria-selected={valid && Number(value) === quantity}
              onClick={() => select(quantity)}
              className={`cursor-pointer px-4 py-2 text-center text-lg font-bold touch-manipulation ${active === quantity ? 'bg-purple-700 text-white' : valid && Number(value) === quantity ? 'bg-purple-100 text-purple-900' : 'text-gray-800 hover:bg-purple-50'}`}
            >
              {quantity}
            </div>
          ))}
        </div>,
        document.fullscreenElement ?? document.body,
      )}
    </div>
  );
}
